import { makeAutoObservable } from 'mobx';
import { load as loadFingerprint } from '@fingerprintjs/fingerprintjs';

import { getBalance, getProfile } from '@/lib/api/auth';
import { logger } from '@/lib/api/logger';
import { sendMsg } from '@/lib/api/postMessageAPI';
import { setFavorites } from '@/lib/api/statistic';
import { LS } from '@/helpers/localStorageHelper';
import { timeout } from '@/helpers/timeout';

/**
 * @typedef Currency
 * @property {undefined=} id - id
 * @property {string} name - currency name
 * @property {string} sign - currency sign
 * @property {number} precision
 * @property {number} limit - currency limit
 */

export class Profile {
  /**
   * @param {import('./RootStoreMobx').RootStoreMobx} root
   */
  constructor(root) {
    makeAutoObservable(this);

    this.root = root;
  }

  /**
   * @type {Array<Currency>}
   */
  currencies = [];

  /**
   * @type {Currency}
   */
  currency = {
    limit: 0,
    name: '',
    precision: 2,
    sign: '',
  };

  ensureTokenPromise = null;

  user = {
    id: undefined,
    name: '',
    playerId: undefined,
    token: null,
    tokenExpiresAt: 0,
    visitorId: null,
  };

  favorite = {};

  /** @type {number | null} */
  balance = null;

  // If "true", it resets the cache when making a request to api/profile.
  isResetCacheInProfileRequest = false;

  //  If user doesn't have token then this is a Guest;
  isGuest = false;

  tokenUsdRate = 0;
  originFrame = '';
  /** @type {{ tokenName: string, logo: string } | null} */
  delistedToken = null;

  setTokenUsdRate = (rate) => {
    this.tokenUsdRate = rate;
  };
  /**
   * @param {{ tokenName: string, logo: string } | null} token
   */
  setDelistedToken = (token = null) => {
    this.delistedToken = token;
  };
  setOriginFrame = (frame = '') => {
    this.originFrame = frame;
  };

  setIsResetCacheInProfileRequest = (bool = false) => {
    this.isResetCacheInProfileRequest = bool;
  };

  updateBalance = async () => {
    const { token, brand: apiKey } = this.root.auth;

    if (!token) {
      return;
    }

    if (!this.root.ui.showBalance) {
      return;
    }

    const { data } = await getBalance({
      apiKey,
      currency: this.currency.name,
      profileId: this.user.id,
      token,
    });

    if (data) {
      this.setBalance(data);
    } else {
      this.setBalance();
    }
  };

  updateFavorites = async (id, opts) => {
    const { token, brand: apiKey } = this.root.auth;

    if (!token) {
      return;
    }

    const { slug = 'tournaments', remove = false } = opts;

    const ids = Array.isArray(id) ? id : [id];

    if (remove) {
      this.favorite[slug] = this.favorite[slug].filter((currId) => !ids.includes(currId));
    } else {
      ids.forEach((currId) => {
        if (!this.favorite[slug].includes(currId)) {
          this.favorite[slug].push(currId);
        }
      });
    }

    setFavorites({ apiKey, token, [slug]: ids }, opts);
  };

  /**
   * @param {{balance?: number}=} data
   */
  setBalance = (data) => {
    const balance = data?.balance;

    if (typeof balance === 'number' && Number.isFinite(balance) && balance >= 0) {
      this.balance = this.roundCurrency(balance, { fn: 'floor' });
    } else {
      this.balance = null;
    }
  };

  /**
   * @param {number} value
   * @param {object} options
   * @param {'round' | 'ceil' | 'floor'=} options.fn
   *
   * @returns {number}
   */
  roundCurrency = (value, { fn = 'round' } = {}) => {
    if (!['round', 'ceil', 'floor'].includes(fn)) {
      throw new Error(`Unexpected rounding function: ${fn}`);
    }

    const tens = 10 ** this.currency.precision;

    let correction = 0;

    if (['round', 'floor'].includes(fn)) {
      // fix for rounding of 4.1, 1.15 or 9.95
      // 1.15 can be fixed with + Number.EPSILON
      // but 9.95 won't be fixed this way unless you use Math.round
      correction = 1e-10;
    }

    return Math[fn]((value + correction) * tens) / tens;
  };

  /**
   * @param {string} token
   */
  setToken = (token) => {
    try {
      const tokenExpiresAt = this.getTokenExpiresAt(token);

      this.user.token = token;
      this.user.tokenExpiresAt = tokenExpiresAt;

      // TODO: added for debug purposes. Remove me in future
      this.user.tokenReceivedAt = Date.now();
    } catch (error) {
      this.user.token = null;
      this.user.tokenExpiresAt = 0;

      throw error;
    }
  };

  /**
   * @param {string=} token
   */
  getTokenExpiresAt = (token) => {
    const encodedHeader = String(token || '')
      .replace(/_/g, '/')
      .replace(/-/g, '+')
      .split('.')[0];

    /** @type {{exp: number; iat: number} | null} */
    let header = null;

    try {
      header = JSON.parse(atob(encodedHeader));
    } catch (error) {
      throw new Error(`Error parsing token: ${error.message}`);
    }

    const exp = Date.now() + (header.exp - header.iat) * 1000;

    if (Number.isNaN(exp)) {
      throw Object.assign(new Error('Error getting token exp value'), {
        encodedHeader,
        exp: header.exp,
        iat: header.iat,
      });
    }

    return exp;
  };

  isTokenExpired = () => {
    const { tokenExpiresAt } = this.user;

    /**
     * The additional time to compensate any latencies and time inconsistencies
     */
    const timeGap = 30000;

    return tokenExpiresAt - timeGap - Date.now() < 0;
  };

  /**
   * Useful method to invalidate token in case when backend returned 401 error
   */
  markTokenExpired = () => {
    this.user.tokenExpiresAt = 0;
  };

  /**
   * @param {object} params
   * @param {boolean=} params.isReTry
   */
  handleProfile = async ({ isReTry } = {}) => {
    const { auth, ui } = this.root;
    const { brand: cid, token: partnerToken } = auth;

    if (!cid) {
      throw new Error('API Key is required for getProfile');
    }

    if (isReTry) {
      await timeout(1);
    }

    try {
      if (!this.user.visitorId) {
        const fpPromise = await loadFingerprint();
        const fingerprint = await fpPromise.get();
        const { visitorId } = fingerprint;
        this.user.visitorId = visitorId;
      }

      const { data: profile } = await getProfile({
        apiKey: cid,
        force: this.isResetCacheInProfileRequest,
        token: partnerToken,
        visitorId: this.user.visitorId,
      });

      this.isResetCacheInProfileRequest = false;

      await this.setProfile(profile);
    } catch (error) {
      if (error.response) {
        // Hide preloader in LayoutGames.jsx if getProfile has error.
        ui.setSocketConfigIsLoaded();
      }

      if (error.request && (!error.response || error.response.status >= 500)) {
        // retry 500 errors or network errors
        // if no error.response — this means Network Error
        await this.handleProfile({ isReTry: true });

        return;
      }

      if (error.response) {
        const {
          status,
          data: { code, message },
        } = error.response;

        // if token is invalid or user not found or token expired
        if (status === 409 && [1, 2].includes(code)) {
          ui.setIsShowSessionExpiredPopup(true);

          sendMsg('session', {
            reason: error.message,
            status: 'error',
          });

          auth.logout();

          throw new UnauthorizedError();
        }

        throw Object.assign(new Error(`Error requesting profile: ${message} (${code})`), {
          cause: error,
          data: error.response.data,
          status: error.response.status,
        });
      }

      throw Object.assign(new Error('Get profile error'), {
        cause: error,
        networkError: true,
      });
    }
  };

  /**
   * @returns {Promise<string>}
   */
  getToken = async () => {
    if (!this.ensureTokenPromise) {
      // re-use the same promise in case when getToken is called concurrently
      this.ensureTokenPromise = Promise.resolve()
        .then(async () => {
          if (this.isTokenExpired()) {
            try {
              await this.handleProfile();
            } catch (error) {
              if (!(error instanceof UnauthorizedError)) {
                logger.error(`Error getting token${error.message ? `: ${error.message}` : ''}`, {
                  error,
                });
              }

              throw error;
            }
          }

          return this.user.token;
        })
        .finally(() => {
          this.ensureTokenPromise = null;
        });
    }

    return this.ensureTokenPromise;
  };

  setProfile = async (user) => {
    const { currency, currencies, id, name, playerId, favorite, token, guest = false } = user;

    const newCurrency = { ...currency, sign: guest ? '' : currency.sign };

    const isSLG = LS.get('isSLG');
    if (isSLG) {
      newCurrency.sign = 'FUN';
      currencies.forEach((currency) => (currency.sign = 'FUN'));
    }

    this.currency = { ...this.currency, ...newCurrency };
    this.currencies = currencies;
    this.favorite = favorite;

    this.user.id = id;
    this.user.name = name;
    this.user.playerId = playerId;
    this.isGuest = guest;

    this.setToken(token);
  };
}

export class UnauthorizedError extends Error {
  constructor(message = 'Unauthorized') {
    super(message);
    this.name = 'UnauthorizedError';
  }
}
